import React, { forwardRef, useState } from 'react';
import BaseInput, { InputProps } from '../_base/Input';
import { altoConsole } from '../../utils/console/console';

type TextFieldComponent = React.ForwardRefExoticComponent<
  TextFieldProps & React.RefAttributes<HTMLInputElement>
> & {
  StartSlot: typeof BaseInput.StartSlot;
  EndSlot: typeof BaseInput.EndSlot;
  Prefix: typeof BaseInput.Prefix;
  Suffix: typeof BaseInput.Suffix;
  StartSelect: typeof BaseInput.StartSelect;
  EndSelect: typeof BaseInput.EndSelect;
  StartIcon: typeof BaseInput.StartIcon;
  EndIcon: typeof BaseInput.EndIcon;
};

export interface TextFieldProps extends InputProps {
  /**
   * Type of text input
   * @default 'text'
   */
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'search' | 'number';

  // Explicitly type onChange to match other form components
  onChange?: (value: string) => void;

  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      type = 'text',
      clearable = false,
      clearButtonTooltip = 'Clear',
      value: controlledValue,
      onChange,
      defaultValue,
      children,
      visuallyHiddenLabel,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    if (!props['label'] && !props['aria-label'] && !props['aria-labelledby']) {
      altoConsole.warn(
        '`label` prop is expected for accessibility compliance',
        { scope: 'TextField' },
      );
    }

    const [uncontrolledValue, setUncontrolledValue] = useState(
      defaultValue || '',
    );
    // Determine the current value (controlled or uncontrolled)
    const value = controlledValue ?? uncontrolledValue;
    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // If controlled, call onChange
      if (controlledValue !== undefined) {
        onChange?.(newValue);
      } else {
        // If uncontrolled, update local state
        setUncontrolledValue(newValue);
      }

      // Call original onChange if provided
      props.inputProps?.onChange?.(e);
    };

    return (
      <BaseInput
        ref={ref}
        {...props}
        visuallyHiddenLabel={visuallyHiddenLabel}
        clearable={clearable}
        clearButtonTooltip={clearButtonTooltip}
        data-testid={dataTestId}
        inputProps={{
          type,
          value,
          onChange: handleChange,
          ...props.inputProps,
        }}
      >
        {React.Children.map(children, (child) =>
          React.isValidElement(child) ? child : null,
        )}
      </BaseInput>
    );
  },
) as TextFieldComponent;

// Add static properties to match BaseInput's subcomponents
TextField.StartSlot = BaseInput.StartSlot;
TextField.EndSlot = BaseInput.EndSlot;
TextField.Prefix = BaseInput.Prefix;
TextField.Suffix = BaseInput.Suffix;
TextField.StartSelect = BaseInput.StartSelect;
TextField.EndSelect = BaseInput.EndSelect;
TextField.StartIcon = BaseInput.StartIcon;
TextField.EndIcon = BaseInput.EndIcon;

// Set displayName for subcomponents
TextField.StartSlot.displayName = 'TextField.StartSlot';
TextField.EndSlot.displayName = 'TextField.EndSlot';
TextField.Prefix.displayName = 'TextField.Prefix';
TextField.Suffix.displayName = 'TextField.Suffix';
TextField.StartSelect.displayName = 'TextField.StartSelect';
TextField.EndSelect.displayName = 'TextField.EndSelect';
TextField.StartIcon.displayName = 'TextField.StartIcon';
TextField.EndIcon.displayName = 'TextField.EndIcon';

TextField.displayName = 'TextField';

export default TextField;
